// src/utils/xlsxCellules.ts — SCE Issues
// Écrire une valeur dans une cellule d'un classeur Excel, par chirurgie XML.
//
// Partagé par les deux endroits où l'appli remplit un VRAI fichier de l'usine
// plutôt que d'en refaire un : l'export « Montage et Tests » (Lot 19) et la
// déclaration NC-S (05/10). Dans les deux cas, tout ce qu'on ne touche pas —
// logo, listes déroulantes, protections, macros — est recopié octet pour octet.
//
// Pur : aucun React, aucun réseau. JSZip est chargé à la demande.

import type JSZipType from 'jszip'

/** Valeur d'une cellule : texte, nombre, ou date (écrite en date Excel). */
export type ValeurCellule = string | number | Date

/** JSZip, chargé seulement quand on écrit un classeur. */
export async function chargerJSZip(): Promise<typeof JSZipType> {
  // Selon l'outil (Vite en navigateur, Node pour les bancs d'essai), le module
  // CommonJS arrive avec ou sans enveloppe `default`.
  const mod = (await import('jszip')) as unknown as { default?: typeof JSZipType } & typeof JSZipType
  return mod.default ?? mod
}

/**
 * Remplace une cellule EXISTANTE de la feuille en gardant son style.
 * Une valeur vide laisse la cellule telle quelle — ou la vide, avec `vider`
 * (un modèle partagé peut avoir gardé une saisie précédente).
 * @throws Error si la cellule n'existe pas (modèle modifié).
 */
export function poserCellule(
  xml: string,
  ref: string,
  valeur: ValeurCellule | undefined,
  { vider = false }: { vider?: boolean } = {},
): string {
  if ((valeur === undefined || valeur === '') && !vider) return xml
  const motif = new RegExp(String.raw`<c r="${ref}"((?:\s+[\w:]+="[^"]*")*)\s*(?:/>|>[\s\S]*?</c>)`)
  const trouve = motif.exec(xml)
  if (!trouve) throw new Error(`Modèle modifié : cellule ${ref} introuvable`)
  const style = /\ss="(\d+)"/.exec(trouve[1] ?? '')?.[1]
  return xml.replace(motif, () => cellule(ref, style, valeur))
}

/** Le XML d'une cellule : texte en ligne, nombre, ou date Excel. */
export function cellule(ref: string, style: string | undefined, valeur: ValeurCellule | undefined): string {
  const s = style ? ` s="${style}"` : ''
  if (valeur === undefined || valeur === '') return `<c r="${ref}"${s}/>`
  if (valeur instanceof Date) return `<c r="${ref}"${s}><v>${dateExcel(valeur)}</v></c>`
  if (typeof valeur === 'number') return `<c r="${ref}"${s}><v>${valeur}</v></c>`
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${echapperXml(valeur)}</t></is></c>`
}

/** Jour de la date locale en numéro de série Excel (1 = 01/01/1900). */
export function dateExcel(d: Date): number {
  return Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(1899, 11, 30)) / 86_400_000)
}

/** Échappe pour le XML (texte ou attribut) et retire les caractères de contrôle qu'il refuse. */
export function echapperXml(texte: string): string {
  return texte
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/\r\n?/g, '\n')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
